import { createMRTColumnHelper, MRT_ColumnDef } from "material-react-table";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import { Button, FoodsTags, LinearProgress } from "@foodpilot/foods";
import { GridText } from "src/components/Grid/GridText";
import { GridJsonAttribute } from "src/components/Grid/GridJsonAttribute";
import { JsonAttributeFilter } from "src/components/Grid/filters/JsonAttributeFilter";
import { useTagsColumn } from "src/components/Grid/hooks/tags/useTagsColumn";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";
import type {
    NetworkQuestionnaireProgress,
    NetworkQuestionnaireStatus,
} from "src/api/client-api/network/useNetworkLists";
import {
    NetworkOptionsFilter,
    NetworkStatusFilter,
    optionLabelsChip,
    type NetworkOption,
    useNetworkStatusLabels,
    useNetworkStatusOptions,
} from "./networkFilters";
import { useShortDateFormatter } from "./useShortDateFormatter";
import { useNetworkAnswersLabels } from "./useNetworkAnswersLabels";

export type NetworkOrganizationFilterOptions = {
    organizations: NetworkOption[];
    sectors: NetworkOption[];
    questionnaires: NetworkOption[];
    countries: NetworkOption[];
};

const STATUS_TAG_VARIANTS = {
    validated: "tags-green",
    in_progress: "tags-blue",
    not_started: "tags-grey",
} as const;

export const NetworkStatusTag = (props: { status: NetworkQuestionnaireStatus }) => {
    const labels = useNetworkStatusLabels();

    return <FoodsTags size="compact" variant={STATUS_TAG_VARIANTS[props.status]} label={labels[props.status]} />;
};

export const useNetworkOrganizationColumns = (
    filterOptions: NetworkOrganizationFilterOptions,
    onOpenOrganization: (row: NetworkQuestionnaireProgress) => void,
): MRT_ColumnDef<NetworkQuestionnaireProgress, any>[] => {
    const { t } = useTranslation();
    const theme = useTheme();
    const formatDate = useShortDateFormatter();
    const statusOptions = useNetworkStatusOptions();
    const { answersAndCompletionLabel } = useNetworkAnswersLabels();
    const { entitiesJsonAttributes } = useFoodpilotContext();
    const { tagColumns = [] } = useTagsColumn<NetworkQuestionnaireProgress>({
        appearance: "product-v2",
        context: "organization",
    });
    const columnHelper = createMRTColumnHelper<NetworkQuestionnaireProgress>();
    const jsonAttributeDefinitions = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.organization] ?? [];

    return [
        columnHelper.accessor("organizationName", {
            id: "organizationName",
            header: t("network.column.organization", "Organization"),
            enableColumnFilter: false,
            Cell: ({ row }) => (
                <Button
                    variant="text"
                    onClick={() => onOpenOrganization(row.original)}
                    aria-label={[row.original.organizationName, row.original.questionnaireTitle]
                        .filter(Boolean)
                        .join(" – ")}
                    sx={{
                        color: theme.palette.primary[600],
                        justifyContent: "flex-start",
                        padding: 0,
                        textAlign: "left",
                    }}
                >
                    {row.original.organizationName}
                </Button>
            ),
        }),
        columnHelper.accessor((row) => row.organizationExternalId ?? "", {
            id: "organizationExternalId",
            header: t("unique_identifier"),
            enableColumnFilter: false,
            enableSorting: false,
            meta: { visibleByDefault: false },
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
        }),
        columnHelper.accessor((row) => row.organizationEmail ?? "", {
            id: "organizationEmail",
            header: t("network.column.organization_email", "Organization email"),
            enableColumnFilter: false,
            enableSorting: false,
            meta: { visibleByDefault: false },
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
        }),
        columnHelper.accessor((row) => row.parentOrganizationNames ?? "", {
            id: "parentOrganization",
            header: t("network.column.parent_organization", "Parent organization"),
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
            Filter: (mrtProps) => <NetworkOptionsFilter mrtProps={mrtProps} options={filterOptions.organizations} />,
            meta: { renderFilterChipValue: optionLabelsChip(filterOptions.organizations) },
        }),
        columnHelper.accessor((row) => row.sectorName, {
            id: "organizationSector",
            header: t("network.column.sector", "Sector"),
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
            Filter: (mrtProps) => <NetworkOptionsFilter mrtProps={mrtProps} options={filterOptions.sectors} />,
            meta: { renderFilterChipValue: optionLabelsChip(filterOptions.sectors) },
        }),
        columnHelper.accessor((row) => row.questionnaireTitle ?? "", {
            id: "ssqId",
            header: t("network.column.questionnaire", "Questionnaire"),
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
            Filter: (mrtProps) => <NetworkOptionsFilter mrtProps={mrtProps} options={filterOptions.questionnaires} />,
            meta: { renderFilterChipValue: optionLabelsChip(filterOptions.questionnaires) },
        }),
        columnHelper.accessor("completionRate", {
            id: "completionRate",
            header: t("network.column.completion", "Completion"),
            enableColumnFilter: false,
            Cell: ({ row }) => (
                <Box sx={{ minWidth: theme.spacing(20), width: "100%" }}>
                    <LinearProgress value={row.original.completionRate} />
                </Box>
            ),
        }),
        columnHelper.accessor("answers", {
            id: "answers",
            header: t("network.column.answers", "Answers"),
            enableColumnFilter: false,
            meta: { visibleByDefault: false },
            Cell: ({ row }) => <GridText appearance="product-v2" value={answersAndCompletionLabel(row.original)} />,
        }),
        columnHelper.accessor("status", {
            id: "status",
            header: t("network.column.status", "Status"),
            Cell: ({ row }) => <NetworkStatusTag status={row.original.status} />,
            Filter: (mrtProps) => <NetworkStatusFilter mrtProps={mrtProps} />,
            meta: { renderFilterChipValue: optionLabelsChip(statusOptions) },
        }),
        columnHelper.accessor((row) => row.lastLoginAt ?? "", {
            id: "lastLoginAt",
            header: t("network.column.last_login", "Last login"),
            enableColumnFilter: false,
            Cell: ({ row }) => (
                <GridText
                    appearance="product-v2"
                    value={
                        row.original.lastLoginAt ?
                            formatDate(row.original.lastLoginAt)
                        :   t("network.last_login.never", "Never")
                    }
                />
            ),
        }),
        columnHelper.accessor((row) => row.country ?? "", {
            id: "country",
            header: t("pays"),
            enableSorting: false,
            meta: { visibleByDefault: false },
            Cell: ({ renderedCellValue }) => <GridText appearance="product-v2" value={renderedCellValue} />,
            Filter: (mrtProps) => <NetworkOptionsFilter mrtProps={mrtProps} options={filterOptions.countries} />,
        }),
        ...tagColumns.map((column) => ({
            ...column,
            enableSorting: false,
            meta: { ...column.meta, visibleByDefault: false },
        })),
        ...jsonAttributeDefinitions.map((definition) =>
            columnHelper.accessor((row) => row.jsonAttributes?.[definition.identifier], {
                id: `jsonAttributes_${definition.identifier}`,
                header: definition.label,
                enableSorting: false,
                meta: { visibleByDefault: false },
                Cell: ({ cell }) => (
                    <GridJsonAttribute
                        value={cell.getValue() as ReactNode}
                        attributeType={definition.type}
                        appearance="product-v2"
                    />
                ),
                Filter: (mrtProps) => <JsonAttributeFilter {...mrtProps} attributeType={definition.type} />,
            }),
        ),
    ];
};
